import { jsx as _jsx } from "react/jsx-runtime";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState, lazy } from "react";
import { ProductResource, ProductSettingsResource } from "@ploy-lib/rest-resources";
import { NetworkErrorBoundary } from "rest-hooks";
import { useResource } from "@rest-hooks/core";
import { ProductSelector, ProductSelectorVariant } from "./ProductSelector";
import { RenderTemplateWithRouteState } from "./renderTemplate";
import { useParsedQueryAndParams, useAbsolutePath } from "@ploy-lib/routing";
import { MissingPage, useNavigationState, useAppActionState, AppActionProvider } from "@ploy-ui/template-form";
import { validApplicationNumber } from "../../utils";
import isEqual from "lodash/isEqual";
import { useInRouterContext, useLocation, useNavigate } from "react-router-dom";
import { NetworkError } from "@ploy-lib/types";
import { makeStyles, Typography } from "@material-ui/core";
const CalculationForm = lazy(() => import("@ploy-ui/calculation-form").then(m => ({
    default: m.CalculationForm
})));
export const ProductView = (props) => {
    const { externalPostback, ...rest } = props;
    return (_jsx(AppActionProvider, { externalPostback: externalPostback, children: _jsx(ProductViewInternal, { ...rest }) }));
};
const useStyles = makeStyles(theme => ({
    header: {
        color: theme.palette.text.primary
    }
}), { name: "DployProductView" });
export const ProductViewInternal = (props) => {
    const { onSubmit, disallowedFieldRoles } = props;
    const classes = useStyles(props);
    const { externalCode, category, enableSelect, formContext, context, wholesale, initialize = true, ext: initialDataFromQuerystring, newapplication, hideHeader } = useParsedQueryAndParams(props);
    const [initialData, setInitialData] = useState(initialDataFromQuerystring);
    useEffect(() => {
        if (!isEqual(initialData, initialDataFromQuerystring))
            setInitialData(initialDataFromQuerystring);
    }, [initialData, initialDataFromQuerystring]);
    const hasRouteState = useInRouterContext();
    const navigation = useNavigationState();
    const appAction = useAppActionState();
    const productFilter = useMemo(() => {
        const filter = {
            active: true,
            creatable: true,
            category,
            wholesale,
            availableInPhoenix: true,
            newapplication
        };
        if (formContext?.toLowerCase() === "calculator") {
            filter.calculator = true;
        }
        return filter;
    }, [category, wholesale, formContext, newapplication]);
    const productSettings = useResource(ProductSettingsResource.detail(), {});
    const filter = !!externalCode && hideHeader ? null : productFilter;
    const products = useResource(ProductResource.list(), filter) || [];
    const [firstProduct] = products;
    const product = useResource(ProductResource.detail(), externalCode ? { externalCode: externalCode } : null);
    const navigate = useNavigate();
    const { search } = useLocation();
    const searchRef = useRef(search);
    useEffect(() => {
        searchRef.current = search;
    }, [search]);
    useEffect(() => {
        if (!externalCode) {
            const firstExternalCode = firstProduct?.externalCode.toLowerCase();
            if (firstExternalCode &&
                productSettings.useDefaultPhoenixDropdownProductSelectorMenu)
                navigate({
                    pathname: firstExternalCode,
                    search: searchRef.current
                }, { replace: true });
        }
    }, [
        product,
        firstProduct,
        enableSelect,
        navigate,
        productSettings.useDefaultPhoenixDropdownProductSelectorMenu,
        externalCode
    ]);
    if (!product && (!enableSelect || !firstProduct)) {
        throw new NetworkError({ status: 404 });
    }
    const handleSubmit = useCallback((values, form) => {
        const { __calculation: { submitResult } = {} } = values;
        if (submitResult?.DoPostBack && appAction.externalPostback) {
            appAction.externalPostback(submitResult);
        }
        else {
            if (submitResult &&
                validApplicationNumber(submitResult.ApplicationNumber)) {
                navigation.application(submitResult.ApplicationNumber, true);
            }
            else if (product && productFilter.calculator) {
                //navigate from calculator to application
                navigation.applicationFromCalculator(product.category.toLocaleLowerCase(), product.externalCode.toLocaleLowerCase());
            }
        }
        form.setSubmitting(false);
    }, [appAction, navigation, product, productFilter.calculator]);
    // Make sure routing in ProductSelector is relative to the product route
    // Need to force this here in case there are other nested routes where the header is mounted.
    // Route wizard state is an example of such a case.
    const basepath = useAbsolutePath("..");
    const header = hideHeader
        ? undefined
        : product &&
            (productSettings.useDefaultPhoenixDropdownProductSelectorMenu ? (_jsx(ProductSelector, { products: products, selected: product, disabled: !enableSelect, basepath: `${basepath}/` })) : (_jsx(Typography, { variant: "h5", className: classes.header, children: product.name })));
    const productExternalCode = (externalCode || product?.externalCode)?.toLocaleUpperCase() || "";
    // Render as an element (not a bare function) so its hooks run in a mounted
    // component, and keep a stable identity so memo(CalculationForm) holds.
    const renderTemplate = useCallback((templateProps) => (_jsx(RenderTemplateWithRouteState, { ...templateProps })), []);
    return (_jsx(Suspense, { fallback: _jsx(MissingPage, { loading: true, header: header }), children: _jsx(NetworkErrorBoundary, { fallbackComponent: ({ error }) => (_jsx(MissingPage, { error: error, header: header })), children: !productSettings.useDefaultPhoenixDropdownProductSelectorMenu &&
                !product ? (_jsx(ProductSelector, { products: products, basepath: `${basepath}/`, variant: ProductSelectorVariant.Buttons })) : (_jsx(CalculationForm, { productExternalCode: productExternalCode, onSubmit: onSubmit || handleSubmit, header: header, formContext: formContext, context: context, skipInitialize: !initialize, initialData: initialData, disallowedFieldRoles: disallowedFieldRoles, children: hasRouteState ? renderTemplate : undefined })) }, productExternalCode) }));
};
//# sourceMappingURL=ProductView.js.map